import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import '../main/MainScreens.css';

export default function CashbackAdminScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    // Check if user is admin
    if (!user?.is_admin) {
      navigate('/home');
      return;
    }
    
    fetchVerifications();
  }, [user, navigate, filter]);

  async function fetchVerifications() {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/cashback-verifications?status=${filter}`);
      setVerifications(data.verifications || []);
    } catch (err) {
      console.error('Failed to fetch verifications:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(verification) {
    if (!window.confirm(`Approve $${(verification.cashback_amount_cents / 100).toFixed(2)} cashback for ${verification.restaurant_name}?`)) {
      return;
    }

    setActionLoading(true);
    try {
      await api.post('/admin/cashback-verifications/approve', {
        verificationId: verification.id,
      });
      
      alert('✅ Cashback approved and added to user balance!');
      setSelectedVerification(null);
      fetchVerifications();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve verification');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject(verification) {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return; // User cancelled

    setActionLoading(true);
    try {
      await api.post('/admin/cashback-verifications/reject', {
        verificationId: verification.id,
        reason: reason || 'Does not meet verification requirements',
      });
      
      alert('❌ Verification rejected');
      setSelectedVerification(null);
      fetchVerifications();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject verification');
    } finally {
      setActionLoading(false);
    }
  }

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="main-screen">
      <MainHeader title="Cashback Admin" showBack={true} />

      <main className="screen-main">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
          
          {/* Filter Tabs */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '2px solid #eee' }}>
            {['pending', 'approved', 'rejected'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                style={{
                  padding: '12px 24px',
                  background: 'none',
                  border: 'none',
                  borderBottom: filter === status ? '3px solid #FF6347' : '3px solid transparent',
                  fontWeight: filter === status ? '600' : '400',
                  color: filter === status ? '#FF6347' : '#666',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {status}
              </button>
            ))}
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading...</p>
          ) : verifications.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No {filter} verifications
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {verifications.map(verification => (
                <div
                  key={verification.id}
                  style={{
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '12px',
                    padding: '20px',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedVerification(verification)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>
                        {verification.restaurant_name}
                      </h3>
                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                        {verification.user_email || `User ID: ${verification.user_id}`}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#FF6347' }}>
                        ${(verification.cashback_amount_cents / 100).toFixed(2)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Bill: ${(verification.bill_amount_cents / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      background: verification.approval_tier === 'high_confidence' ? '#e8f5e9' : 
                                 verification.approval_tier === 'medium_confidence' ? '#fff3e0' : '#fee',
                      color: verification.approval_tier === 'high_confidence' ? '#2e7d32' : 
                             verification.approval_tier === 'medium_confidence' ? '#f57c00' : '#c33',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                    }}>
                      {verification.approval_tier.replace('_', ' ')}
                    </span>
                    <span style={{
                      padding: '4px 12px',
                      background: '#f0f0f0',
                      borderRadius: '12px',
                      fontSize: '12px',
                    }}>
                      {verification.order_type === 'dine_in' ? '🍽️ Dine-In' :
                       verification.order_type === 'delivery' ? '🚗 Delivery' : '🥡 Pickup'}
                    </span>
                    {verification.delivery_platform && (
                      <span style={{
                        padding: '4px 12px',
                        background: '#f0f0f0',
                        borderRadius: '12px',
                        fontSize: '12px',
                      }}>
                        {verification.delivery_platform}
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {verification.distance_meters !== null && (
                      <div>📍 {Math.round(verification.distance_meters)}m from restaurant</div>
                    )}
                    <div>⏱️ {parseFloat(verification.hours_since_decision).toFixed(1)}h after decision</div>
                    <div>📅 {new Date(verification.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Detail Modal */}
          {selectedVerification && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px',
              }}
              onClick={() => setSelectedVerification(null)}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  maxWidth: '600px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  padding: '24px',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 style={{ margin: '0 0 20px 0' }}>Verification Details</h2>

                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#666' }}>Restaurant</h3>
                  <p style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                    {selectedVerification.restaurant_name}
                  </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#666' }}>Receipt/Photo</h3>
                  <img
                    src={selectedVerification.photo_url}
                    alt="Receipt"
                    style={{
                      maxWidth: '100%',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>Bill Amount</h3>
                    <p style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                      ${(selectedVerification.bill_amount_cents / 100).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>Cashback</h3>
                    <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#FF6347' }}>
                      ${(selectedVerification.cashback_amount_cents / 100).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>Order Type</h3>
                    <p style={{ margin: 0, fontSize: '16px' }}>
                      {selectedVerification.order_type === 'dine_in' ? '🍽️ Dine-In' :
                       selectedVerification.order_type === 'delivery' ? '🚗 Delivery' : '🥡 Pickup'}
                    </p>
                  </div>
                  {selectedVerification.delivery_platform && (
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>Platform</h3>
                      <p style={{ margin: 0, fontSize: '16px' }}>
                        {selectedVerification.delivery_platform}
                      </p>
                    </div>
                  )}
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>Distance</h3>
                    <p style={{ margin: 0, fontSize: '16px' }}>
                      {selectedVerification.distance_meters !== null 
                        ? `${Math.round(selectedVerification.distance_meters)}m`
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>Timing</h3>
                    <p style={{ margin: 0, fontSize: '16px' }}>
                      {parseFloat(selectedVerification.hours_since_decision).toFixed(1)}h
                    </p>
                  </div>
                </div>

                {selectedVerification.verification_status === 'pending' && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                    <button
                      onClick={() => handleApprove(selectedVerification)}
                      disabled={actionLoading}
                      style={{
                        flex: 1,
                        padding: '14px',
                        background: '#4CAF50',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        opacity: actionLoading ? 0.6 : 1,
                      }}
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => handleReject(selectedVerification)}
                      disabled={actionLoading}
                      style={{
                        flex: 1,
                        padding: '14px',
                        background: '#f44336',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        opacity: actionLoading ? 0.6 : 1,
                      }}
                    >
                      ❌ Reject
                    </button>
                  </div>
                )}

                <button
                  onClick={() => setSelectedVerification(null)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginTop: '12px',
                    background: '#f0f0f0',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
}
